import "./PaymentStatus.css";

import { faCheck, faExclamation } from "@fortawesome/free-solid-svg-icons";
import { search, sendReceipt } from "../../../api/infringement";
import { setAccount, setCurrentStep } from "../../../store/fine/fineActions";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CustomInput from "../../CustomInput/CustomInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Footer from "../../../components/Footer/Footer";
import { HandbackData } from "../../../models/paymentStatusModel";
import Header from "../../../components/Header/Header";
import ReactLoading from "react-loading";
import ResponseModel from "../../../models/responseModel";
import moment from "moment";
import { sendHandbackResponse } from "../../../api/infringement";
import { setPaymentStatus } from "../../../store/fine/paymentStatusActions";
import { setHandbackStatus as storeHandbackStatus } from "../../../store/fine/handbackActions";
import appConfig from "../../../config";
import ReCaptcha from 'react-google-recaptcha'

const PaymentStatus = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [handbackData, setHandbackData] = useState<HandbackData | null>(null);
  const [handbackStatus, setHandbackStatus] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [disabled, setDisabled] = useState<boolean>(false);
  const [visibleToken, setVisibleToken] = useState<any>(false);
  const captchaRef = useRef<any>(null);
  const [numberTries, setNumberTries] = useState(1)
  const APIKEY = numberTries % 3 !== 0 ? appConfig.recaptchaSiteKey : appConfig.recpatchaSiteKeyv2;

  let status = searchParams.get("status");
  let digiapiExternalRef = searchParams.get("digiapiExternalRef");
  let payatTranId = searchParams.get("payatTranId");
  let payatReferenceNum = searchParams.get("payatReferenceNum");
  let digiapiResponseMsg = searchParams.get("digiapiResponseMsg");
  const paymentStatus = useAppSelector(
    (state) => state.paymentStatus.paymentStatus
  );

  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (!payatReferenceNum) navigate("/");
    if (payatReferenceNum && status) {
      if (!paymentStatus) dispatch(setPaymentStatus(status));
      setLoading(true);
      sendHandbackResponse(payatReferenceNum, status)
        .then(({ status, data }: ResponseModel) => {
          dispatch(storeHandbackStatus(status));
          setHandbackStatus(status);
          if (status) {
            setHandbackData(data);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    if (handbackData) {
      if (handbackData.warrant_pending === "1" && !user) navigate("/");
    }
  }, [handbackData]);

  const onSaveAddressClicked = () => {
    if (email) {
      sendReceipt(payatReferenceNum!, email).then(
        ({ status }: ResponseModel) => {
          if (status) {
            setEmailSubmitted(true);
          }
        }
      );
    }
  };

  const onTryAgainClicked = async (t: string, version: boolean) => {
    handleTryAgain(t, version);
  };

  const handleTryAgain = (token: string, version: boolean) => {
    setLoading(true);
    search(payatReferenceNum!, token, version)
      .then((resp: any) => {
        const { status, data } = resp;
        if (status) {
          if (data.warrantOfArrestPending) {
            dispatch(setCurrentStep(3));
          } else {
            dispatch(setCurrentStep(2));
          }
          dispatch(setAccount(payatReferenceNum!));
          dispatch(setPaymentStatus(null));
          navigate("/fine");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExecuteRecaptcha = async (submitted = true) => {
    let token: string;

    if (captchaRef.current != null) {
      if (submitted) {
        token = await captchaRef.current?.executeAsync();
      } else {
        token = await captchaRef.current.getValue();

      }

      setNumberTries(numberTries + 1)
      setDisabled(false)
    if (submitted) {
      handleTryAgain(token, (numberTries) % 3 !== 0)
    } else {
      setVisibleToken({token, version:(numberTries) % 3 !== 0})
    }
    }
  }

  const onBackToHomeClicked = () => {
    navigate("/");
  };

  const renderStatus = () => {
    if (paymentStatus) {
      switch (paymentStatus) {
        case "success":
          return renderProcessing();
        case "processing":
          return renderProcessing();
        case "failed":
          return renderFailed();
        case "cancelled":
          return renderCancelled();
        default:
          return null;
      }
    } else {
      return null;
    }
  };

  const renderProcessing = () => (
    <div className="payment-status-content">
      <div className="payment-status-heading">
        <FontAwesomeIcon
          className="payment-icon"
          icon={faCheck}
          size="3x"
          color="#18283c"
        />
        <p>Processing</p>
      </div>
      <p className="payment-status-description">
        <b>Thank you for completing the Pay@ payment process.</b>
        <br />
        We are verifying your payment. Please note that this can take up to 36
        hours.
      </p>
      {!user ? (
        <div className="want-a-receipt-container">
          {!emailSubmitted ? (
            <>
              <p className="want-a-receipt-heading">
                Want to receive a receipt when we verify payment?
              </p>
              <CustomInput
                label="EMAIL ADDRESS"
                placeholder="Email Address"
                value={email}
                setValue={setEmail}
              />
              <div
                className="save-address-button"
                onClick={() => onSaveAddressClicked()}
              >
                Submit
              </div>
            </>
          ) : (
            <>
              <p className="want-a-receipt-heading">
                On verifying a successful payment, we’ll send your receipt to
                you.
              </p>
              <p className="want-a-receipt-subheading">
                Kindly note this can take up to 36 hours.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="want-a-receipt-container">
          <p className="want-a-receipt-heading">
            We're busy verifying your payment
          </p>
          <p className="want-a-receipt-subheading">
            We'll email you your receipt once this process is complete
          </p>
        </div>
      )}
      <div className="payment-status-receipt-preview">
        {handbackData?.full_name && (
          <div className="line-item">
            <p className="label">Full Name:</p>
            <p className="value">{handbackData?.full_name}</p>
          </div>
        )}
        <div className="line-item">
          <p className="label">Payment Date:</p>
          <p className="value">{moment().format("YYYY-MM-DD")}</p>
        </div>
        {handbackData?.account_number && (
          <div className="line-item">
            <p className="label">Notice Number:</p>
            <p className="value">{handbackData?.account_number}</p>
          </div>
        )}
        {payatReferenceNum && (
          <div className="line-item">
            <p className="label">Reference Number:</p>
            <p className="value">{digiapiExternalRef}</p>
          </div>
        )}
        {payatTranId && (
          <div className="line-item">
            <p className="label">Transaction ID:</p>
            <p className="value">{payatTranId}</p>
          </div>
        )}
        {handbackData?.fine_amount && handbackData?.warrant_pending !== "1" && (
          <div className="line-item">
            <p className="label">Amount:</p>
            <p className="value">R{Number(handbackData?.fine_amount)! / 100}</p>
          </div>
        )}
        {handbackData?.warrant_pending && (
          <div className="line-item">
            <p className="label">Warrant Pending:</p>
            <p className="value">
              {handbackData?.warrant_pending === "1" ? "Yes" : "No"}
            </p>
          </div>
        )}
        {handbackData?.warrant_pending === "1" &&
          handbackData.contempt_court_amount && (
            <div className="warrant-line-item-container">
              <div className="warrant-line-item">
                <p className="label">Fine Amount:</p>
                <p className="value">
                  R
                  {Number(handbackData?.fine_amount) / 100 -
                    Number(handbackData?.contempt_court_amount) / 100}
                </p>
              </div>
              <div className="warrant-line-item">
                <p className="label">Contempt of Court Amount:</p>
                <p className="value">
                  R{Number(handbackData?.contempt_court_amount) / 100}
                </p>
              </div>
              <div className="warrant-line-item">
                <p className="label" style={{ fontWeight: 700 }}>
                  Total Amount:
                </p>
                <p className="value" style={{ fontWeight: 700 }}>
                  R{Number(handbackData?.fine_amount) / 100}
                </p>
              </div>
            </div>
          )}
        {handbackData?.institution && (
          <div className="line-item">
            <p className="label">Institution:</p>
            <p className="value">{handbackData?.institution}</p>
          </div>
        )}
      </div>
      {handbackData?.warrant_pending === "1" && (
        <p className="admission-confirmation">
          The offender has confirmed the admission of guilt
        </p>
      )}
      <p
        className="payment-status-description"
        style={{ marginTop: "5%", marginBottom: "3%" }}
      >
        Should you have any queries regarding this receipt, the CoCT help desk
        can be reached via telephone and email:
      </p>
      <div className="payment-status-receipt-preview" style={{ marginTop: 5 }}>
        <div className="line-item">
          <p className="label">Telephone (24/7):</p>
          <p className="value">0860 103 089</p>
        </div>
        <div className="line-item">
          <p className="label">Email:</p>
          <p className="value">traffic.representation@capetown.gov.za</p>
        </div>
      </div>
      <div className="pay-another-button" onClick={() => navigate("/")}>
        <p>Pay another fine</p>
      </div>
    </div>
  );

  const renderFailed = () => (
    <div className="payment-status-content">
      <div className="payment-status-heading">
        <FontAwesomeIcon
          className="payment-icon"
          icon={faExclamation}
          size="3x"
          color="#18283c"
        />
        <p>Payment Failed</p>
      </div>
      <p className="payment-status-description">{digiapiResponseMsg}</p>
      {numberTries % 3 !== 0 ? (<>
        <ReCaptcha
            theme="light"
            size={"invisible"}
            ref={captchaRef}
            sitekey={APIKEY}
        />
      </>) : (
          <ReCaptcha
              size={"normal"}
              ref={captchaRef}
              style={{marginTop: 10}}
              sitekey={APIKEY}
              onChange={(e: any) => handleExecuteRecaptcha(false)}
          />
      )}
      <div
        className="try-again-button"
        onClick={() => {
          if (!visibleToken) {
            if (!loading) {
              handleExecuteRecaptcha(true);
            }
          } else {
            if (!loading) onTryAgainClicked(visibleToken.token, visibleToken.version);
          }
          // !loading && onTryAgainClicked()}
        }}
      >
        {!loading ? (
          <p>Try again</p>
        ) : (
          <div
            style={{
              marginTop: "-8%",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ReactLoading
              type="bubbles"
              color="white"
              height={"10%"}
              width={"10%"}
            />
          </div>
        )}
      </div>
      <div
        className="back-to-home-payment-button"
        onClick={() => onBackToHomeClicked()}
      >
        Return to home
      </div>
    </div>
  );
  const renderCancelled = () => (
    <div className="payment-status-content">
      <div className="payment-status-heading">
        <FontAwesomeIcon
          className="payment-icon"
          icon={faExclamation}
          size="3x"
          color="#18283c"
        />
        <p>Payment Cancelled</p>
      </div>
      <p className="payment-status-description">{digiapiResponseMsg}</p>
      <div
        className="try-again-button"
        onClick={() => {
          if (!visibleToken) {
            if (!loading) handleExecuteRecaptcha(true);
          } else {
            if (!loading) onTryAgainClicked(visibleToken.token, visibleToken.version);
          }
        }
      }
      >
        {!loading ? (
          <p>Try again</p>
        ) : (
          <div style={{ marginTop: "-5%" }}>
            <ReactLoading
              type="bubbles"
              color="white"
              height={"10%"}
              width={"10%"}
            />
          </div>
        )}
      </div>
      <div
        className="back-to-home-payment-button"
        onClick={() => onBackToHomeClicked()}
      >
        Return to home
      </div>
       {numberTries % 3 !== 0 ? (<>
        <ReCaptcha
            theme="light"
            size={"invisible"}
            ref={captchaRef}
            sitekey={APIKEY}
        />
      </>) : (
          <ReCaptcha
              size={"normal"}
              ref={captchaRef}
              style={{marginTop: 10}}
              sitekey={APIKEY}
              onChange={(e: any) => handleExecuteRecaptcha(false)}
          />
      )}
    </div>
  );

  const renderError = () => (
    <div className="payment-status-content">
      <div className="payment-status-heading">
        <FontAwesomeIcon
          className="payment-icon"
          icon={faExclamation}
          size="3x"
          color="#18283c"
        />
        <p>Unable to view receipt</p>
      </div>
      <p>Your link has expired</p>
      <div
        className="back-to-home-payment-button"
        onClick={() => onBackToHomeClicked()}
      >
        Return to home
      </div>
    </div>
  );

  return (
    <div className="payment-status-container">
      <Header />
      {!loading ? (
        handbackData && handbackStatus ? (
          renderStatus()
        ) : handbackStatus !== null && !handbackStatus ? (
          renderError()
        ) : null
      ) : (
        // Change size of loader on mobile
        <ReactLoading
          type="bubbles"
          color="#e97700"
          height={"10%"}
          width={"10%"}
        />
      )}
      <Footer />
    </div>
  );
};

export default PaymentStatus;
